/**
 * What the app shows about a late fee — admin modal AND customer portal.
 *
 * ---------------------------------------------------------------------------
 * This file exists because the modal used to decide for itself, from the wrong
 * field. It read `preview.lateFee.isOverdue` -- which by design ignores the
 * grace period, because OVERDUE and LATE-FEE-DUE are deliberately different
 * questions -- and rendered "Apply Late Fee ($75.00) (Loan is overdue)" with an
 * enabled checkbox on a loan three days past due inside a seven-day grace
 * window. The server would have refused the fee, but the admin was invited to
 * charge it and told the loan warranted it.
 *
 * The rule, unchanged and NOT reimplemented here:
 *   OVERDUE        = next_payment_date < today. No grace involved.
 *   LATE FEE DUE   = days past due EXCEEDS the loan's own grace_period_days,
 *                    and not waived.
 *   Grace gates the FEE only, never the overdue flag.
 *
 * Every flag below is the server's own answer, computed by
 * loanLateFeeAppliesSql() / loanPastGracePeriodSql() / LOAN_LATE_FEE_WAIVED_SQL
 * and delivered on the loan row by GET /api/admin/loans, GET /api/loans and
 * GET /api/loans/:id. Nothing here recomputes a date. A second implementation
 * of this rule is the bug this file prevents.
 *
 * ---------------------------------------------------------------------------
 * WHY BOTH AUDIENCES LIVE IN ONE FILE
 *
 * The customer portal shipped its own fourth version of the rule: a hardcoded
 * `daysOverdue >= 8` in Dashboard.js, a day count that was off by one against
 * the server's, the TENANT's configured fee amount instead of the loan's, and
 * no waiver check at all. A buyer on a 14-day-grace contract was told on day 8
 * that "a $75 late fee has been applied" when the server had charged nothing.
 *
 * So the CLASSIFICATION is shared -- lateFeeStateOf() below is the single
 * reading of the server's flags, and both describeLateFee() (admin) and
 * describeCustomerLateFee() (buyer) go through it. The WORDING is not shared,
 * because the audiences want different things: the admin needs a checkbox label
 * and a control state ("Apply Late Fee ($50.00)", disabled, unchecked), the
 * buyer needs to know whether money has actually been taken and what happens
 * next. Merging the strings would hand one of them the other's UI. Sharing the
 * classifier is what stops the rule forking again.
 * ---------------------------------------------------------------------------
 */

/** Four states. Only `offered` may ever charge a fee. */
export const LATE_FEE_STATE = Object.freeze({
  NOT_OVERDUE: 'not_overdue',
  WITHIN_GRACE: 'within_grace',
  WAIVED: 'waived',
  OFFERED: 'offered'
});

const dayWord = (n) => (Number(n) === 1 ? 'day' : 'days');

const money = (n) => {
  const v = Number(n);
  return Number.isFinite(v)
    ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';
};

/**
 * Format a DATE column for display without letting it slip a day.
 *
 * `next_payment_date` and `late_fee_starts_on` are Postgres DATEs, serialised as
 * an ISO instant at UTC midnight. `new Date(thatString)` west of Greenwich lands
 * on the previous calendar day, which would show a buyer a grace deadline one
 * day early. Replacing the dashes with slashes forces the local-time parse.
 */
export const formatLoanDate = (value) => {
  if (!value) return '';
  const datePart = String(value).split('T')[0];
  const parsed = new Date(datePart.replace(/-/g, '/'));
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric'
  });
};

/**
 * Which of the four states this loan is in, read from the server's flags.
 *
 * THE single reading of the rule. Nothing else in the client may branch on
 * days_past_due, grace_period_days or next_payment_date to answer this.
 *
 * @param {object} loan a loan row carrying is_overdue / late_fee_applies /
 *                      late_fee_waived
 */
export const lateFeeStateOf = (loan) => {
  if (loan?.is_overdue !== true) return LATE_FEE_STATE.NOT_OVERDUE;
  if (loan?.late_fee_applies === true) return LATE_FEE_STATE.OFFERED;
  if (loan?.late_fee_waived === true) return LATE_FEE_STATE.WAIVED;
  return LATE_FEE_STATE.WITHIN_GRACE;
};

/**
 * The fee this loan would be charged.
 *
 * Mirrors the server exactly (`loan.late_fee_amount || configLateFee` in both
 * the preview and record routes), including the `||` -- a stored 0 falls back
 * to the tenant default there, so it must here, or the label would promise $0
 * on a loan the server charges the default for.
 *
 * Customer-facing rows carry the server's own resolution of this as
 * `effective_late_fee_amount`; it wins when present, so the portal never has to
 * be told the tenant default at all.
 */
export const lateFeeAmountFor = (loan, defaultLateFeeAmount) =>
  Number(loan?.effective_late_fee_amount)
  || Number(loan?.late_fee_amount)
  || Number(defaultLateFeeAmount)
  || 0;

/**
 * @param {object} loan  a row from GET /api/admin/loans
 * @param {object} opts  { defaultLateFeeAmount } - tenant default from config
 * @returns {{state:string, visible:boolean, disabled:boolean, checked:boolean,
 *            amount:number, label:string, hint:string|null, tone:string}}
 */
export const describeLateFee = (loan, opts = {}) => {
  const amount = lateFeeAmountFor(loan, opts.defaultLateFeeAmount);
  const days = Number(loan?.days_past_due) || 0;
  const grace = Number(loan?.grace_period_days);
  const graceText = Number.isFinite(grace) ? `${grace}-day grace period` : 'grace period';

  switch (lateFeeStateOf(loan)) {
    // 1. Nothing is late. There is no question in the admin's mind to answer, so
    //    the control is not rendered at all.
    case LATE_FEE_STATE.NOT_OVERDUE:
      return {
        state: LATE_FEE_STATE.NOT_OVERDUE,
        visible: false, disabled: true, checked: false, amount,
        label: '', hint: null, tone: 'none'
      };

    // 2. Late fee is genuinely due. Offered and checked, exactly as before -- the
    //    admin may still untick it to decline to charge a fee that IS owed.
    case LATE_FEE_STATE.OFFERED:
      return {
        state: LATE_FEE_STATE.OFFERED,
        visible: true, disabled: false, checked: true, amount,
        label: `Apply Late Fee ($${money(amount)})`,
        hint: `${days} ${dayWord(days)} late, past the ${graceText}`,
        tone: 'due'
      };

    // 3. Past grace but already waived for this due date. Shown, disabled, and
    //    named as a waiver rather than a grace period: the reason matters, and
    //    reversing a waiver is a deliberate act, not a checkbox in a payment
    //    modal.
    case LATE_FEE_STATE.WAIVED:
      return {
        state: LATE_FEE_STATE.WAIVED,
        visible: true, disabled: true, checked: false, amount,
        label: `Apply Late Fee ($${money(amount)})`,
        hint: `${days} ${dayWord(days)} late. Late fee waived for this due date, nothing to charge`,
        tone: 'waived'
      };

    // 4. Overdue but inside grace. Shown DISABLED rather than hidden: the admin
    //    is looking at a loan they already know is late, so "is there a late
    //    fee?" is a live question. An empty space answers it by implication and
    //    invites them to go looking; a disabled row with the reason on it answers
    //    it and closes it.
    default:
      return {
        state: LATE_FEE_STATE.WITHIN_GRACE,
        visible: true, disabled: true, checked: false, amount,
        label: `Apply Late Fee ($${money(amount)})`,
        hint: `${days} ${dayWord(days)} late. Within ${graceText}, no late fee due`,
        tone: 'grace'
      };
  }
};

/**
 * What the BUYER is told about the late fee on their own loan.
 *
 * Same four states, same server flags, different job. Three rules govern the
 * copy, and they are the reason this function exists:
 *
 *   1. Never say a fee "has been applied" unless late_fee_applies is true. The
 *      old dashboard said it from a day count and was wrong twice over.
 *   2. A waived fee is never presented as charged. The buyer is told plainly
 *      that they do not owe it.
 *   3. The grace period quoted is the LOAN's own (effective_grace_period_days,
 *      frozen at origination and printed in Section 26 of their signed
 *      contract), never the tenant's current Business Settings value.
 *
 * Tone: this is read by someone who is behind on a payment and may be
 * frightened. It states facts, gives them the date that matters, and does not
 * threaten.
 *
 * @param {object} loan a row from GET /api/loans or GET /api/loans/:id
 * @returns {{state:string, visible:boolean, tone:string, heading:string,
 *            body:string[], amount:number, daysPastDue:number,
 *            gracePeriodDays:number|null, chargesOn:string}}
 */
export const describeCustomerLateFee = (loan) => {
  const state = lateFeeStateOf(loan);
  const amount = lateFeeAmountFor(loan);
  const days = Number(loan?.days_past_due) || 0;
  const graceRaw = Number(loan?.effective_grace_period_days);
  const grace = Number.isFinite(graceRaw) ? graceRaw : null;
  const dueOn = formatLoanDate(loan?.next_payment_date);
  const chargesOn = formatLoanDate(loan?.late_fee_starts_on);

  const base = { state, amount, daysPastDue: days, gracePeriodDays: grace, chargesOn };

  const wasDue = dueOn
    ? `Your payment was due on ${dueOn}, ${days} ${dayWord(days)} ago.`
    : `Your payment is ${days} ${dayWord(days)} past due.`;

  // Always "-day", never "-days": a hyphenated compound adjective stays
  // singular ("a 14-day grace period"), whatever the number.
  const graceText = `${grace}-day grace period`;

  switch (state) {
    case LATE_FEE_STATE.NOT_OVERDUE:
      return { ...base, visible: false, tone: 'none', heading: '', body: [] };

    // Past grace and not waived: the fee is real and the buyer owes it. Say the
    // amount, say it plainly, and use the LOAN's figure -- the tenant's current
    // default is not what this contract carries.
    case LATE_FEE_STATE.OFFERED:
      return {
        ...base,
        visible: true,
        tone: 'due',
        heading: 'A late fee has been added',
        body: [
          grace === null
            ? wasDue
            : `${wasDue} That is past the ${graceText} in your contract.`,
          `A $${money(amount)} late fee has been added to what you owe.`,
          'Please make your payment when you are able. If something has changed and ' +
            'you need to talk about your options, get in touch. We would much rather ' +
            'hear from you than not.'
        ]
      };

    // Waived. The buyer must never be asked for money that was forgiven, and
    // must not be left wondering whether it is still hanging over them.
    case LATE_FEE_STATE.WAIVED:
      return {
        ...base,
        visible: true,
        tone: 'waived',
        heading: 'Your payment is late: no late fee',
        body: [
          wasDue,
          `The $${money(amount)} late fee for this payment has been waived. You do not owe it.`,
          'Please make your payment when you are able, or contact us if you need to ' +
            'talk about your options.'
        ]
      };

    // Overdue but inside the grace window. NOTHING has been charged. Saying so
    // outright is the whole point: the old copy claimed the opposite.
    default:
      return {
        ...base,
        visible: true,
        tone: 'grace',
        heading: 'Your payment is late',
        body: [
          wasDue,
          grace === null
            ? 'No late fee has been charged.'
            : `No late fee has been charged. Your contract allows a ${graceText} ` +
              'after the due date.',
          chargesOn
            ? `If we have not received your payment by ${chargesOn}, a $${money(amount)} ` +
              'late fee will be added.'
            : `A $${money(amount)} late fee applies once the grace period ends.`
        ]
      };
  }
};

/**
 * How soon a payment is treated as "coming up" on the buyer's screen.
 *
 * A presentation choice, not a rule about money: it decides when a blue
 * reminder chip appears, nothing else. Kept as a named constant so it is
 * obvious that it is NOT a grace period and must never be read as one.
 */
export const UPCOMING_PAYMENT_REMINDER_DAYS = 5;

/**
 * The one-line status chip above a loan card.
 *
 * Severity comes from the loan's STATE, not from a day count: amber while the
 * buyer is inside the grace period their contract gives them, red once a fee
 * has genuinely attached. The old version bucketed at 5/15/30 days, so a buyer
 * on a 14-day-grace contract got a red 15-day badge while still owing no fee.
 *
 * Both day counts are the server's (days_past_due, days_until_due). This
 * function does no date arithmetic.
 *
 * @param {object} loan a row from GET /api/loans or GET /api/loans/:id
 * @returns {{text:string, color:string, icon:string}|null}
 */
export const overdueBadgeFor = (loan) => {
  if (!loan || loan.status === 'paid_off' || !loan.next_payment_date || loan.alerts_disabled) {
    return null;
  }

  const late = Number(loan.days_past_due) || 0;

  if (late > 0) {
    const label = `${late} ${dayWord(late) === 'day' ? 'Day' : 'Days'}`;
    // Red only once the loan is past its own grace period. Whether the fee was
    // then waived is the notice's business, not the chip's -- the payment is
    // still outstanding either way.
    return loan.past_grace_period === true
      ? { text: `${label} Past Due`, color: 'var(--color-error)', icon: '⚠️' }
      : { text: `${label} Late`, color: 'var(--color-warning-text)', icon: '⏰' };
  }

  const until = Number(loan.days_until_due);
  if (Number.isFinite(until) && until <= UPCOMING_PAYMENT_REMINDER_DAYS) {
    return {
      text: until === 0
        ? 'Payment Due Today'
        : `Payment Due in ${until} ${until === 1 ? 'Day' : 'Days'}`,
      color: 'var(--color-info)',
      icon: '📅'
    };
  }

  return null;
};

export default describeLateFee;
